import { useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Checkbox from '@zenbusiness/zui/Checkbox';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';

import { BUSINESS_WEBSITE_INVALID_MESSAGE } from './constants/errorMessages';
import { regexWebsite } from './utils/regex';

export const BusinessWebsiteUIField = ({
  field = { setSaved: () => {}, setLoading: () => {} },
  data,
  dataChangeHandler = () => {},
  disabled
}) => {
  const [website, setWebsite] = useState({
    businessWebsiteUrl: data?.businessWebsiteUrl ?? '',
    noWebsite: Boolean(data?.noWebsite),
    valid: true,
    isDirty: false,
    isFocus: false,
    disableCheckbox: Boolean(data?.businessWebsiteUrl)
  });

  const isShowError = website.isDirty && !website.valid && !website.isFocus;

  const trackBusinessWebsiteChangeClick = useButtonPressedCallback({
    elementText: 'business_website',
    elementName: 'banking_collect_info',
    appPageName: 'banking_application'
  });
  const onDataChanged = (newValue) => {
    if (newValue.valid) {
      field.setLoading();
      /**
       * Tracking Details: Tracking to be added to the banking application Collect Info screen.
       * Question:
       *          Where are customers typically dropping off on this screen?
       *          Do they typically drop off at SSN?
       *          How often are they viewing the user agreements?
       */
      trackBusinessWebsiteChangeClick();
    }

    dataChangeHandler(
      newValue.valid,
      {
        businessWebsiteUrl: newValue.businessWebsiteUrl,
        noWebsite: newValue.noWebsite
      },
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave, isError, newValue.valid);
        forceUpdate();
      }
    );
  };

  const handleChangeRequired = () => {
    const noWebsite = !website.noWebsite;
    const newValues = {
      ...website,
      businessWebsiteUrl: '',
      valid: noWebsite,
      noWebsite
    };
    setWebsite(newValues);
    document.getElementById('website').value = '';

    onDataChanged(newValues);
  };

  const handleBlur = (event) => {
    const newValue = event.target.value;
    if (!newValue || newValue !== website.businessWebsiteUrl || !website.valid) {
      const newValues = {
        ...website,
        valid: regexWebsite.test(newValue),
        businessWebsiteUrl: newValue,
        isDirty: true,
        isFocus: false
      };
      setWebsite(newValues);
      onDataChanged(newValues);
    }
  };

  const handleFocus = () => {
    setWebsite({ ...website, isFocus: true });
  };

  const handleChange = (event) => {
    const disableCheckbox = Boolean(event.target.value);
    if (disableCheckbox !== website.disableCheckbox) {
      setWebsite({
        ...website,
        disableCheckbox
      });
    }

    dataChangeHandler(false, {}, (forceUpdate, { isSave, isError }) => {
      field.setSaved(isSave, isError, false);
      forceUpdate();
    });
  };

  return (
    <Stack spacing="24px">
      <Stack spacing="4px">
        <TextField
          disabled={disabled || website.noWebsite}
          error={isShowError}
          id="website"
          inputProps={{ defaultValue: data?.businessWebsiteUrl }}
          label="Enter your business website or social media profile URL"
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          type="text"
        />
        {isShowError && (
          <Text
            color="red"
            component="div"
            pt="8px"
            size="xs"
          >
            {BUSINESS_WEBSITE_INVALID_MESSAGE}
          </Text>
        )}
      </Stack>
      <FormControlLabel
        control={
          <Checkbox
            checked={website.noWebsite}
            color="default"
            disabled={website.disableCheckbox || disabled}
            id="noWebsite"
            onChange={handleChangeRequired}
          />
        }
        label={<Text size="xl">I don’t have a business website</Text>}
        margin="normal"
      />
    </Stack>
  );
};

BusinessWebsiteUIField.propTypes = {
  data: PropTypes.shape({
    businessWebsiteUrl: PropTypes.string,
    noWebsite: PropTypes.bool
  }),
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({ setSaved: PropTypes.func, setLoading: PropTypes.func }),
  disabled: PropTypes.bool
};
